import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../../App"
import { logger } from "../../App/Logger"
import { Platform } from "../../App/Platform"
import { Session } from "../../App/Session"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { EditGroup, EditGroupData } from "../../Components/EditGroup"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { OverlayScrollBody } from "../../Components/OverlayScrollBody"
import { ShipSelectViewData } from "./Context"
import { companionHandoffTimeoutMs, createCompanionConnect } from "./Connect"
import { CompanionConnectError, CompanionConnectPhase, CompanionConnectState, CompanionResolveReason } from "./Params"

// Copy key per failure reason. A reason without a key would render nothing, so the
// lookup is total over the enum -- the type checker enforces that, which is what keeps
// "the form appeared with no explanation" from coming back (#1388).
const errorKeys: Record<CompanionConnectError, string> = {
  [CompanionConnectError.timeout]: "connect_logbook_timeout",
  [CompanionConnectError.notDiscovered]: "connect_logbook_not_discovered",
  [CompanionConnectError.ambiguous]: "connect_logbook_ambiguous",
  [CompanionConnectError.unresolved]: "connect_logbook_unresolved",
  [CompanionConnectError.unreachable]: "connect_logbook_unreachable",
  [CompanionConnectError.refused]: "connect_logbook_refused",
  [CompanionConnectError.notClaimed]: "connect_logbook_not_claimed",
  [CompanionConnectError.notAccessible]: "connect_logbook_not_accessible",
  [CompanionConnectError.addressInvalid]: "connect_logbook_address_invalid",
  [CompanionConnectError.ownDevice]: "connect_logbook_own_device",
  [CompanionConnectError.failed]: "connect_logbook_failed",
};

export type CompanionCloseGuard = () => Promise<boolean>;

interface CompanionConnectViewProps {
  shipId: number;
  shipName: string;
  // Whether an automatic (Bonjour) attempt is worth making. False also covers a ship
  // deliberately omitted from discovery because its advertisements were ambiguous, so
  // it means "no unambiguous automatic route", never "not on the network".
  nearby: boolean;
  onCancel: () => void;
  // Header/backdrop owners must await this before unmounting the dialog.
  onCloseGuard?: (guard: CompanionCloseGuard | undefined) => void;
  // Rendered below the form (the overlay's own cancel/disconnect actions), so this
  // component owns the connect flow without owning the surrounding overlay's actions.
  footer?: ReactNode | ((cancel: () => Promise<void>) => ReactNode);
  // Overrides the automatic attempt's deadline. Only the catalog sets it, so the
  // timed-out state can be rendered without a fixture waiting four seconds.
  timeoutMs?: number;
  handoffTimeoutMs?: number;
  handoff?: (shipId: number) => void;
}

// The single companion-connect surface. Opens with a spinner while the automatic route
// is attempted, then either completes or reveals the manual address form carrying the
// reason it fell back. Replaces the previous split where LAN-discovered ships silently
// auto-connected (with no error path at all) and everything else got a bare form, which
// is what left users with a button that did nothing (#1279).
export function CompanionConnectView({ shipId, shipName, nearby, onCancel, onCloseGuard, footer, timeoutMs, handoffTimeoutMs = companionHandoffTimeoutMs, handoff }: CompanionConnectViewProps) {
  const app = GetApp();
  const t = app.getT("AppOverlayContentView");
  const [state, setState] = useState<CompanionConnectState>({ phase: CompanionConnectPhase.connecting });
  const closeRequest = useRef<Promise<boolean> | undefined>(undefined);

  // The workflow outlives individual renders, so keep one instance per mounted dialog.
  const connect = useMemo(() => createCompanionConnect({
    shipId,
    resolve: async (id) => {
      try {
        const response = await ShipSelectViewData.resolveLocal(app.apiClient, id);
        return { address: response?.address, reason: response?.reason as CompanionResolveReason | undefined };
      } catch (error) {
        // A resolve that fails as a REQUEST tells us nothing about the network -- the
        // route reports a real failure to resolve in its `reason`, not by throwing. The
        // connect attempt below still reports its own outcome.
        logger.debug("companion resolve failed", id, error);
        return undefined;
      }
    },
    connectLocal: (id, address, attempt) => ShipSelectViewData.connectLocal(app.apiClient, id, address, attempt),
    cancelLocal: (id, attempt) => ShipSelectViewData.cancelLocal(app.apiClient, id, attempt),
    prepareHandoff: async (id) => (
      await ShipSelectViewData.prepareHandoff(app.apiClient, id)
    ).url,
    onSuccess: async (id, destination) => {
      // The destination's LoginView selects the ship after redeeming the handoff.
      // Until then this origin still owns an account session, not an open logbook.
      if (handoff) {
        handoff(id);
      } else {
        if (!destination) {
          throw new Error("Companion preparation returned no destination");
        }
        Session.beginCompanion(id);
        window.location.assign(destination);
      }
    },
    onState: setState,
    timeoutMs,
    handoffTimeoutMs,
  }), [shipId, timeoutMs, handoffTimeoutMs, handoff]);

  // The reason the dialog fell back, as a sentence. Every reason resolves to copy that
  // says what happened AND what to do about it -- the ship name and the address the
  // attempt used are woven in where they help the user act on it.
  const errorMessage = useMemo(() => {
    if (!state.error) {
      return undefined;
    }
    return t(errorKeys[state.error], { name: shipName, address: state.address || "" });
  }, [state.error, state.address, shipName, t]);

  // Start once per dialog. `nearby` is captured at open time on purpose: discovery is
  // volatile, and the flow must not change shape underneath the user mid-attempt.
  useEffect(() => {
    connect.start({ nearby }).catch((error) => logger.error("companion connect failed", error));
    return () => {
      void connect.dispose().catch((error) => logger.error("companion cancellation failed", error));
    };
  }, [connect]);

  useEffect(() => {
    if (state.phase !== CompanionConnectPhase.success) {
      return;
    }
    const timer = setTimeout(() => {
      setState((previous) => ({ phase: CompanionConnectPhase.manual, address: previous.address, error: CompanionConnectError.unreachable }));
    }, handoffTimeoutMs);
    return () => clearTimeout(timer);
  }, [state.phase, handoffTimeoutMs]);

  const prepareClose = useCallback((): Promise<boolean> => {
    if (closeRequest.current) {
      return closeRequest.current;
    }
    const request = (async () => {
      try {
        await connect.cancel();
        Session.clearCompanion(shipId);
        return true;
      } catch (error) {
        logger.error("companion cancellation failed", error);
        setState({ phase: CompanionConnectPhase.manual, error: CompanionConnectError.failed });
        return false;
      }
    })();
    closeRequest.current = request;
    void request.finally(() => {
      if (closeRequest.current === request) {
        closeRequest.current = undefined;
      }
    });
    return request;
  }, [connect, shipId]);

  useEffect(() => {
    onCloseGuard?.(prepareClose);
    return () => onCloseGuard?.(undefined);
  }, [onCloseGuard, prepareClose]);

  const cancel = useCallback(async () => {
    if (await prepareClose()) {
      onCancel();
    }
  }, [prepareClose, onCancel]);

  // This device's own build number, reported by its local `/api/status`. Shown so a
  // user (or support) can say which version is actually installed -- the primary's
  // required version is deliberately not fetched, since that would mean another round
  // trip on a path that has just failed.
  const version = app.status?.version;

  // Where this platform actually gets its updates. A "download the latest version"
  // link is wrong on iOS and Android, where sideloading isn't possible and the store
  // is the only route; a store instruction is equally useless on desktop, which has
  // no store entry at all.
  const updateAdvice = useMemo(() => {
    switch (Platform.of(app)) {
      case "ios":
        return t("connect_logbook_outdated_appstore");
      case "android":
        return t("connect_logbook_outdated_playstore");
      default:
        return (
          <a href={`https://logbook.yachts/site/${app.language}/download`} target="_blank" rel="noreferrer">
            {t("connect_logbook_outdated_download")}
          </a>
        );
    }
  }, [app, app.language, t]);

  const submit = useCallback(async (_data: FormData, values: EditGroupData) => {
    await connect.submit(values.address as string);
  }, [connect]);

  if (state.phase == CompanionConnectPhase.connecting || state.phase == CompanionConnectPhase.success) {
    return (
      <OverlayScrollBody className="Overlay-body message-text-bigger Overlay-body-padding" readMoreLabel={t("read_more")} deps={[state.phase, state.error]}>
        <Block>
          <BlockRow className="companion-connect-progress">
            <BlockField>
              <span className="Loading">{t("connect_logbook_connecting", { name: shipName })}</span>
            </BlockField>
          </BlockRow>
          <BlockRow actions={true} className="block-row-one-row">
            <BlockField>
              <button className="button" onClick={cancel}>{t("cancel")}</button>
            </BlockField>
          </BlockRow>
        </Block>
      </OverlayScrollBody>
    );
  }

  // The primary refused this device for running an older build. Deliberately NOT
  // the manual address form: no address changes the outcome, so offering one would
  // invite the user to keep retrying something that cannot work. Show the only
  // remedy there is -- update the app -- routed to wherever this platform actually
  // gets its updates.
  if (state.phase == CompanionConnectPhase.outdated) {
    return (
      <OverlayScrollBody className="Overlay-body message-text-bigger Overlay-body-padding" readMoreLabel={t("read_more")} deps={[state.phase, state.error]}>
        <Block>
          <BlockRow>
            <BlockField><h4>{t("connect_logbook_outdated_title")}</h4></BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField>{t("connect_logbook_outdated", { name: shipName })}</BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField>{updateAdvice}</BlockField>
          </BlockRow>
          {version && (
            <BlockRow>
              <BlockField className="companion-connect-version">
                {t("connect_logbook_outdated_version", { version })}
              </BlockField>
            </BlockRow>
          )}
          {footer == null && (
            <BlockRow actions={true} className="block-row-one-row">
              <BlockField>
                <button className="button" onClick={cancel}>{t("cancel")}</button>
              </BlockField>
            </BlockRow>
          )}
        </Block>
        {typeof footer === "function" ? footer(cancel) : footer}
      </OverlayScrollBody>
    );
  }

  return (
    <OverlayScrollBody className="Overlay-body message-text-bigger Overlay-body-padding" readMoreLabel={t("read_more")} deps={[state.phase, state.error]}>
      <Block>
        <BlockRow>
          <LocalizedMessage className="no-distance" message={(m) => m.noteText.shipOpen} replace={{ name: shipName }} />
        </BlockRow>
      </Block>
      <Block>
        <EditGroup manualSave={true} hideButton={true} onSave={submit} editable={true} editableFields={["address"]}>
          {errorMessage && (
            <BlockRow>
              <BlockField className="error-message">
                {errorMessage}
              </BlockField>
            </BlockRow>
          )}
          <BlockRow className="block-row-last">
            <BlockField label={t("connection_host")} className="block_field_small_20">
              <input name="address" required defaultValue={state.address || ""} />
            </BlockField>
            <BlockField>
              <button className="button default" type="submit" disabled={state.submitting === true}>
                {state.submitting === true ? t("connect_logbook_connecting_short") : t("connect_logbook")}
              </button>
            </BlockField>
          </BlockRow>
        </EditGroup>
      </Block>
      <Block>
        <BlockRow className="block-row-top20 block-row-0">
          <LocalizedMessage message={(m) => m.noteText.shipOpen2} replace={{ name: shipName }} />
        </BlockRow>
      </Block>
      {typeof footer === "function" ? footer(cancel) : footer}
    </OverlayScrollBody>
  );
}
